import React, { useCallback, useEffect, useMemo, useState } from "react";
import ClassNames from "classnames";
import {
  EditorContent,
  useEditor,
  useEditorState,
  type AnyExtension,
} from "@tiptap/react";
import type { EditorView } from "@tiptap/pm/view";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import HardBreak from "@tiptap/extension-hard-break";
import Code from "@tiptap/extension-code";
import CodeBlock from "@tiptap/extension-code-block";
import { BulletList, OrderedList, ListItem } from "@tiptap/extension-list";
import Heading from "@tiptap/extension-heading";
import Image from "@tiptap/extension-image";
import { CharacterCount, Placeholder, UndoRedo } from "@tiptap/extensions";
import "./TextEditor.scss";
import { FieldViewBlock, Tooltip } from "../../components";
import FormFieldError from "../FormFieldError/FormFieldError";
import {
  getDataAnalyticsProps,
  REQUIRED_ERROR_MESSAGE,
  formatNumber,
} from "../../../utils";
import {
  convertValueToTextEditorContent,
  convertMentionsToTokensInHtml,
  insertImageIntoView,
} from "./TextEditorConvertor";
import {
  ClassSpanMark,
  ClassMark,
  HintExtension,
  getRichFormattingClassNames,
  getRichFormattingExtensions,
  getMentionExtension,
  resolveRichFormattingExtensions,
  type AutoCompleteSettings,
  type RichFormattingExtension,
} from "./Extensions";
import { handleKeyDown, handlePaste } from "./HandleDomEvents";
import TextEditorToolbar from "./TextEditorToolbar";
import {
  DEFAULT_TOOLBAR_ACTIONS,
  getRequiredExtensions,
  type ToolbarItem,
} from "./TextEditorToolbarConfig";

export { TOOLBAR_DIVIDER, type ToolbarItem } from "./TextEditorToolbarConfig";

export const validateTextEditorValue = (
  value: { html?: string; text?: string } | null
) => {
  // Check text first, then fall back to html for initial load scenarios
  // where only html is provided before the editor's onCreate populates text
  return !!(value?.text?.length || value?.html?.length)
    ? true
    : REQUIRED_ERROR_MESSAGE;
};

export interface ErrorProps {
  message: string;
  type: string;
}

export interface TextEditorProps {
  value?: { html?: string; text?: string } | null;
  onChange?: (value: { html: string; text: string }) => void;
  autoCompleteSettings?: AutoCompleteSettings;
  placeholder?: string;
  wrapperClassName?: string;
  customHeight?: string;
  label?: string;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  error?: ErrorProps;
  description?: string;
  maxCharacters?: number;
  labelTop?: boolean;
  required?: boolean;
  hideLabelHelper?: boolean;
  noMargin?: boolean;
  isInline?: boolean;
  isLarge?: boolean;
  size?: "max" | "custom";
  labelWidth?: string | number | boolean;
  withHelp?: string;
  viewMode?: boolean;
  applyAutoFocus?: boolean;
  "data-testid"?: string;
  hasToolbar?: boolean;
  /**
   * Toolbar buttons, in order. Defaults to bold + italic.
   * Strike, highlight and align* actions also enable the extension they need.
   */
  toolbarActions?: ToolbarItem[];
  /**
   * Opt-in extensions: strike (`<s>`), highlight (plain `<mark>`), textAlign (`text-align-*` class).
   * Needed for read-only rendering, where no toolbar enables them.
   * Changing the resulting extension set (with `toolbarActions`) recreates the editor from `value`.
   */
  richFormatting?: RichFormattingExtension[];
  mentionMaxResults?: number;
  hint?: string;
  allowImages?: boolean;
  hasBorder?: boolean;
}

interface TextEditorFieldProps
  extends Omit<TextEditorProps, "richFormatting" | "toolbarActions"> {
  toolbarActions: ToolbarItem[];
  enabledExtensions: readonly RichFormattingExtension[];
}

const TextEditorField: React.FC<TextEditorFieldProps> = ({
  value,
  onChange = () => {},
  autoCompleteSettings,
  placeholder = "",
  wrapperClassName = "",
  customHeight,
  label = null,
  name,
  disabled = false,
  readOnly = false,
  onBlur = () => {},
  onFocus = () => {},
  error,
  maxCharacters,
  labelTop = false,
  required = false,
  hideLabelHelper = false,
  noMargin = false,
  isInline = false,
  isLarge = false,
  size = "max",
  labelWidth = false,
  withHelp,
  viewMode = false,
  applyAutoFocus = false,
  description = "",
  "data-testid": dataTestId = "text-editor",
  hasToolbar = true,
  toolbarActions,
  enabledExtensions,
  hint,
  allowImages = false,
  hasBorder = true,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const contentValue = useMemo(() => {
    return convertValueToTextEditorContent(value);
  }, [value?.html, value?.text]);

  const normalizeHtml = useCallback((html: string) => {
    return convertValueToTextEditorContent({ html: html || "" }).html || "";
  }, []);

  const isEditorEditable = !disabled && !readOnly && !viewMode;

  const editor = useEditor({
    extensions: [
      Bold,
      Italic,
      Document,
      Paragraph,
      Text,
      ...(isInline || !allowImages
        ? []
        : [
            Image.configure({
              inline: false,
              allowBase64: true,
              HTMLAttributes: {
                class: "text-editor-image",
              },
            }),
          ]),
      ...(!!placeholder
        ? [
            Placeholder.configure({
              placeholder,
            }),
          ]
        : []),
      ClassSpanMark,
      ClassMark,
      ...(isInline ? [] : [HardBreak]),
      OrderedList,
      BulletList,
      ListItem,
      Code,
      CodeBlock,
      Heading.configure({
        levels: [1, 2, 3, 4],
      }),
      ...getRichFormattingExtensions(enabledExtensions),
      // getMentionExtension returns null when no autocomplete is configured;
      // tiptap tolerates falsy entries at runtime, so we keep the value and
      // launder the type to satisfy the Extensions[] array element type.
      getMentionExtension(autoCompleteSettings) as AnyExtension,
      UndoRedo.configure({
        depth: 1,
      }),
      ...(!!maxCharacters
        ? [
            CharacterCount.configure({
              limit: maxCharacters,
            }),
          ]
        : []),
      ...(hint && isEditorEditable ? [HintExtension.configure({ hint })] : []),
    ],
    content: contentValue.html || "",
    editable: isEditorEditable,
    autofocus: applyAutoFocus ? "end" : false,
    editorProps: {
      attributes: {
        // `name` is an optional prop and may be undefined at runtime; prosemirror
        // tolerates an undefined attribute value (it is skipped when applied to the
        // DOM). Launder the type to satisfy the `{ [name: string]: string }` shape
        // without coercing the runtime value.
        name: name as string,
        class: "text-editor-wrapper",
      },
      handleDOMEvents: {
        keydown: (_view: EditorView, event: KeyboardEvent) => {
          if (!isInline) {
            return false;
          }
          return handleKeyDown(event);
        },
        paste: (view: EditorView, event: ClipboardEvent) => {
          if (isInline || !isEditorEditable || !allowImages) {
            return false;
          }

          return handlePaste(event, view, insertImageIntoView);
        },
      },
    },
    onCreate: ({ editor: currentEditor }) => {
      // in order to utilize the editor text and html values, we need to set the initial value
      const html = convertMentionsToTokensInHtml(currentEditor.getHTML());
      const text = currentEditor.getText();
      onChange?.({ html, text });
    },
    onFocus: () => {
      setIsFocused(true);
      onFocus?.();
    },
    onBlur: () => {
      setIsFocused(false);
      onBlur?.();
    },
    onUpdate: ({ editor: currentEditor }) => {
      const html = convertMentionsToTokensInHtml(currentEditor.getHTML());
      const text = currentEditor.getText();
      onChange?.({ html, text });
    },
  });

  const { charactersCount } = useEditorState({
    editor,
    selector: (context) => ({
      charactersCount:
        context.editor?.storage?.characterCount?.characters?.() ??
        context.editor?.getText().length ??
        0,
    }),
  });

  useEffect(() => {
    editor?.setEditable(isEditorEditable, false);
  }, [editor, isEditorEditable]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    // If the consumer isn't controlling the value (or temporarily produced `undefined`),
    // don't wipe the editor content.
    if (typeof value === "undefined") {
      return;
    }

    const nextHtml = contentValue.html || "";
    const normalizedNextHtml = normalizeHtml(nextHtml);

    // If the editor is already at this value (after normalization), don't reset content
    // (this avoids disrupting selection and "eating" keystrokes like space).
    if (normalizeHtml(editor.getHTML()) === normalizedNextHtml) {
      return;
    }

    if (!nextHtml) {
      editor.commands.clearContent(false);
    } else {
      editor.commands.setContent(nextHtml, { emitUpdate: false });
    }
  }, [editor, contentValue.html, normalizeHtml]);

  const classNameWrapper = ClassNames(
    "input-wrap",
    "input-wrap-text-editor",
    wrapperClassName && wrapperClassName,
    {
      "has-label": label !== null,
      "has-label-top": labelTop,
      "hide-label-helper": hideLabelHelper,
      "is-disabled": disabled,
      "has-read-only": readOnly,
      "has-no-margin": noMargin,
      "has-size-max": size === "max",
      "has-label-width": !!labelWidth,
      "has-size-custom": size === "custom",
      "is-large": isLarge,
      "has-error": !!error,
      "is-focused": isFocused,
    }
  );

  const surfaceClassName = ClassNames(
    "text-editor-surface",
    getRichFormattingClassNames(enabledExtensions),
    {
      "is-focused": isFocused,
      "is-disabled": disabled,
      "has-error": !!error,
      "is-inline": isInline,
      "is-read-only": readOnly,
      "is-hint-visible": !!hint && isFocused,
      "has-border": hasBorder,
    }
  );

  const surfaceStyle = customHeight ? { height: customHeight } : undefined;

  const labelStyle =
    labelWidth && labelWidth !== true
      ? {
          width:
            typeof labelWidth === "number" ? `${labelWidth}px` : labelWidth,
        }
      : undefined;

  const shouldShowToolbar = !readOnly && hasToolbar && !isInline;

  if (viewMode) {
    return (
      <FieldViewBlock
        label={label || ""}
        text={editor?.getText() || ""}
        data-testid={`${dataTestId}-text-editor`}
      />
    );
  }

  return (
    <div className={classNameWrapper}>
      {label && (
        <label className="input-label" htmlFor={name} style={labelStyle}>
          {label}
          {!required && !hideLabelHelper && (
            <span className={"label-helper"}>Optional</span>
          )}
          {withHelp && (
            <Tooltip type="info" content={withHelp}>
              <i className={`seem-icon seem-icon-help`} />
            </Tooltip>
          )}
        </label>
      )}

      {description && <div className={`input-description`}>{description}</div>}

      <div
        className={`input-holder ${disabled ? "is-disabled" : ""}`}
        data-testid={dataTestId}
        {...getDataAnalyticsProps(props)}
      >
        <div className={surfaceClassName} style={surfaceStyle}>
          {shouldShowToolbar && editor && (
            <TextEditorToolbar
              editor={editor}
              items={toolbarActions}
              dataTestId={dataTestId}
            />
          )}

          <div className="text-editor-content">
            <EditorContent editor={editor} />
          </div>
          {typeof maxCharacters === "number" && isFocused && (
            <div className="text-editor-content-bottom">
              <div
                className={ClassNames("text-editor-char-count", {
                  "is-over-limit": charactersCount > maxCharacters,
                })}
                data-testid={`${dataTestId}-char-count`}
              >
                {formatNumber(charactersCount)} / {formatNumber(maxCharacters)}
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <FormFieldError
          errorMessage={error.message}
          errorType={error.type}
          isOpen={!isFocused}
          fieldName={dataTestId}
        />
      )}
    </div>
  );
};

// Keyed by the extension set: TipTap registers extensions only at creation, so a new set needs a new editor.
const TextEditor: React.FC<TextEditorProps> = ({
  richFormatting = [],
  toolbarActions = DEFAULT_TOOLBAR_ACTIONS,
  ...props
}) => {
  const enabledExtensions = resolveRichFormattingExtensions([
    ...richFormatting,
    ...getRequiredExtensions(toolbarActions),
  ]);

  return (
    <TextEditorField
      key={enabledExtensions.join(",")}
      {...props}
      toolbarActions={toolbarActions}
      enabledExtensions={enabledExtensions}
    />
  );
};

export default TextEditor;
